import {
  HHAlert,
  HHDivider,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { useFormContext } from 'react-hook-form';
import { PackageType, ScopeableType } from '../../../types';
import { PackageScopeSelect } from './package-scope-select';
import { ProductOfferingMultiSelect } from './product-offering-multi-select';
import { ProgramScopeSelect } from './program-scope-select';
import {
  SCOPE_SELECTOR_ERROR_TEST_ID,
  SCOPE_SELECTOR_GLOBAL_HINT_TEST_ID,
} from './scope-selector-constants';
import { ScopeTypeRadio } from './scope-type-radio';

export interface ScopeSelectorProps {
  availablePackages: PackageType[];
  disabled?: boolean;
  entityLabel?: string;
}

interface ScopeExplanation {
  label: string;
  description: string;
}

const buildScopeExplanations = (entityLabel: string): ScopeExplanation[] => [
  {
    label: 'Everything',
    description: `Applies the ${entityLabel} to every package, program, and product offering.`,
  },
  {
    label: 'Package',
    description:
      'Applies only to a single package (e.g. all programs and offerings under that package).',
  },
  {
    label: 'Product offering',
    description:
      'Applies only to the selected product offering(s) — the most granular target.',
  },
  {
    label: 'Program (legacy)',
    description: 'Applies to a program type (Chronic or Acute).',
  },
];

export const ScopeSelector = ({
  availablePackages,
  disabled,
  entityLabel = 'discount',
}: ScopeSelectorProps): JSX.Element => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const scopeableType = watch('scopeableType') as ScopeableType;
  const scopesError = errors.scopes;
  const scopesErrorMessage =
    typeof scopesError?.message === 'string' ? scopesError.message : undefined;

  const handleTypeChange = (): void => {
    setValue('scopes', [], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const explanations = buildScopeExplanations(entityLabel);

  return (
    <HHStack direction="column" spacing={3}>
      <HHStack
        spacing={0.5}
        sx={{
          p: 2,
          borderRadius: 1,
          backgroundColor: 'grey.50',
          border: '1px solid',
          borderColor: 'grey.200',
        }}
        data-testid="scope-selector-explainer"
      >
        <HHTypography hhVariant="body2" sx={{ fontWeight: 600 }}>
          Choose what this {entityLabel} applies to
        </HHTypography>
        {explanations.map(e => (
          <HHTypography
            key={e.label}
            hhVariant="caption"
            color="text.secondary"
          >
            <strong>{e.label}</strong> — {e.description}
          </HHTypography>
        ))}
        <HHDivider sx={{ my: 0.5 }} />
        <HHTypography hhVariant="caption" color="text.secondary">
          <strong>Precedence:</strong> when a charge matches multiple{' '}
          {entityLabel}s, the most specific scope wins. Product offering →
          Package → Program → Everything.
        </HHTypography>
      </HHStack>
      <ScopeTypeRadio
        disabled={disabled}
        disabledTypes={[ScopeableType.Program]}
        onTypeChange={handleTypeChange}
      />
      {scopeableType === ScopeableType.Global && (
        <HHTypography
          hhVariant="body2"
          color="GrayText"
          data-testid={SCOPE_SELECTOR_GLOBAL_HINT_TEST_ID}
        >
          This {entityLabel} will apply to all packages, programs, and product
          offerings.
        </HHTypography>
      )}
      {scopeableType === ScopeableType.Package && (
        <PackageScopeSelect
          availablePackages={availablePackages}
          disabled={disabled}
          error={
            scopesErrorMessage
              ? { type: 'scopes', message: scopesErrorMessage }
              : undefined
          }
        />
      )}
      {scopeableType === ScopeableType.Program && (
        <HHStack spacing={1.5}>
          <HHAlert
            hhVariant="outlined"
            severity="warning"
            data-testid="scope-selector-program-legacy-warning"
          >
            <HHTypography hhVariant="body2">
              Program scope is a legacy targeting mode and is being phased out.
              For new {entityLabel}s, prefer <strong>Package</strong> or{' '}
              <strong>Product offering</strong> instead.
            </HHTypography>
          </HHAlert>
          <ProgramScopeSelect
            disabled={disabled}
            error={
              scopesErrorMessage
                ? { type: 'scopes', message: scopesErrorMessage }
                : undefined
            }
          />
        </HHStack>
      )}
      {scopeableType === ScopeableType.ProductOffering && (
        <ProductOfferingMultiSelect
          disabled={disabled}
          error={
            scopesErrorMessage
              ? { type: 'scopes', message: scopesErrorMessage }
              : undefined
          }
        />
      )}
      {scopeableType === ScopeableType.Global && scopesErrorMessage && (
        <HHTypography
          hhVariant="caption"
          color="error"
          data-testid={SCOPE_SELECTOR_ERROR_TEST_ID}
        >
          {scopesErrorMessage}
        </HHTypography>
      )}
    </HHStack>
  );
};
